import * as Sentry from '@sentry/browser'
import PropTypes from 'prop-types'
import {
  branch,
  compose,
  lifecycle,
  setPropTypes,
  withHandlers,
  withStateHandlers,
  withProps
} from 'recompose'
import { connect } from 'react-redux'
import { DirectMessage } from 'src/constants/trackingEvents'
import QuestionSingle from './QuestionSingle'
import { sendQuestion } from 'src/api/mikasa/requests'
import { messageSponsor, messageAgent } from 'src/api/node/requests'
import { flashNotification } from 'src/actions'
import { injectIntl } from 'react-intl'
import { QUESTIONS, ERRORS } from 'src/constants/messages'
import { selectRouteKey, selectUserIdPair } from 'src/store/selectors/layout'
import {
  selectCustomerProfile,
  selectCustomerHasSponsor
} from 'src/store/selectors/customerProfile'
import { selectSponsorshipId } from 'src/store/selectors/sponsorship'
import { selectHomeId } from 'src/store/selectors/home'
import { selectBuyerId } from 'src/store/selectors/buyerInfo'
import { dispatchEvent } from 'src/helpers/tagManager'
import { trackHomeActivity } from 'src/actions/home'

const buyerContainer = connect(
  state => ({
    sendHandler: ({ topic, message }) =>
      sendQuestion(selectBuyerId(state), {
        topic,
        message
      }),
    customerProfile: selectCustomerProfile(state)
  }),
  dispatch => ({
    notify: message =>
      dispatch(flashNotification({ message, messageType: 'error' }))
  })
)

const homeownerContainer = connect(
  (state, { disableCospo, trackActivityForCospo }) => ({
    sendHandler: ({ topic, message }, track) => {
      const homeId = selectHomeId(state)
      const customerProfile = selectCustomerProfile(state)
      let messageFunc = messageAgent

      if (!customerProfile) {
        return
      }

      if (!disableCospo && selectCustomerHasSponsor(state)) {
        messageFunc = (homeId, options) => {
          trackActivityForCospo && track && track(topic, message)
          return messageSponsor(homeId, options)
        }
      }

      return messageFunc(homeId, {
        topic,
        message,
        agentId: customerProfile.legacyRef,
        sponsorId: selectSponsorshipId(state),
        clientId: selectUserIdPair(state).legacyId
      })
    },
    customerProfile: selectCustomerProfile(state)
  }),
  dispatch => ({
    notify: message =>
      dispatch(flashNotification({ message, messageType: 'error' })),
    track: (topic, message) =>
      dispatch(
        trackHomeActivity({
          // TODO: Potential to move this elsewhere with proposed consolodation of messageAgent/Sponsor.
          event: 'messageAgent',
          source: 'user',
          properties: {
            message,
            topic
          }
        })
      )
  })
)

const propTypes = {
  disableCospo: PropTypes.bool
}

export const enhance = compose(
  setPropTypes(propTypes),
  withProps(() => ({
    isBuyer: selectRouteKey() === 'buy'
  })),
  branch(({ isBuyer }) => isBuyer, buyerContainer, homeownerContainer),
  withStateHandlers(
    ({ question }) => ({
      inputValue: question || '',
      isLoading: false
    }),
    {
      clearInput: () => () => ({ inputValue: '' }),
      onChange: () => e => ({ inputValue: e.target.value }),
      setLoading: () => isLoading => ({ isLoading })
    }
  ),
  lifecycle({
    UNSAFE_componentWillUpdate (nextProps) {
      if (this.props.question !== nextProps.question) {
        this.props.onChange({ target: { value: nextProps.question } })
      }
    }
  }),
  withStateHandlers(
    { isLoading: false },
    {
      setLoading: () => value => ({ isLoading: value })
    }
  ),
  injectIntl,
  withHandlers({
    onSubmit: ({
      sendHandler,
      inputValue,
      intl,
      notify,
      questionTopic,
      setLoading,
      onSubmit,
      clearOnSubmit,
      clearInput,
      track,
      customerProfile
    }) => async e => {
      e.preventDefault()
      if (!customerProfile) {
        notify([
          intl.formatMessage(ERRORS.defaultHeadline),
          intl.formatMessage(ERRORS.defaultMessage)
        ])
        Sentry.captureMessage(
          'Unable to send dm because customerProfile is not present'
        )
        return
      }
      setLoading(true)

      try {
        await sendHandler(
          {
            topic: questionTopic,
            message: inputValue
          },
          track
        )
        dispatchEvent(DirectMessage, {
          eventLabel: questionTopic
        })
        setTimeout(() => {
          setLoading(false)
          notify(intl.formatMessage(QUESTIONS.sendSuccess))
          if (onSubmit) onSubmit(true, clearInput)
        }, 1000)
      } catch (err) {
        // handle question error
        Sentry.captureException(err)
        setLoading(false)
        notify(intl.formatMessage(QUESTIONS.sendFailure))
        if (onSubmit) onSubmit(false, clearInput)
      }
    }
  })
)

export default enhance(QuestionSingle)
